'use client';

import { type ReactNode, useCallback, useState } from 'react';

import type { BioContext } from '@ps/functions/bio/bioContext';
import { bioUrl } from '@ps/functions/bio/bioUrls';
import { PopStoreExtendedSubscriptionLevel } from '@ps/models/lib/creator-subscription';
import type { CreatorInfo } from '@ps/types/creator';

import { ChannelListMasterPane } from '../../community-channels/components/channel-list/ChannelListMasterPane';
import { CommunityChannelsPanes } from '../../community-channels/components/channel-list/CommunityChannelsPanes';
import { CommunityProvider } from '../../community-channels/contexts/community/provider';
import type { CommunityChannel } from '../../community-channels/types/CommunityChannel';
import { BioUserAccessProvider } from '../contexts/user-access/provider';
import { useBioUserAccess } from '../contexts/user-access/useBioUserAccess';
import { ChannelSubscribeBlockerModal } from './ChannelSubscribeBlockerModal';

interface Props {
  /** The bio context (facts) for the creator being viewed — the single source
   *  for the creator id + every in-bio URL (channel links, subscribe blocker). */
  bioContext: BioContext;
  /** The creator's display name — shown on the list header + join blocker. */
  creatorName: string;
  /** The creator's avatar — shown on the join blocker's creator card. */
  creatorAvatar?: string;
  /** The creator's unified identity — threaded to the login-brand handoff when a
   *  logged-out viewer taps "Join this Channel" (so `/login` shows the creator). */
  creatorInfo: CreatorInfo;
  /** The active channel detail (route children). */
  children: ReactNode;
}

/**
 * Visitor community shell: mounts the subscription-standing context +
 * the community provider pointed at the bio creator's store, then lays out the
 * shared master (channel list) / detail panes. The signed-in `AuthProvider` /
 * `UserProfileProvider` are supplied by the route layout above.
 */
export function VisitorCommunityShell({
  bioContext,
  creatorName,
  creatorAvatar,
  creatorInfo,
  children,
}: Props) {
  return (
    <BioUserAccessProvider creatorId={bioContext.creatorId}>
      <VisitorCommunityProvider
        bioContext={bioContext}
        creatorName={creatorName}
        creatorAvatar={creatorAvatar}
        creatorInfo={creatorInfo}
      >
        <CommunityChannelsPanes
          master={<ChannelListMasterPane />}
          detail={children}
        />
      </VisitorCommunityProvider>
    </BioUserAccessProvider>
  );
}

/**
 * Bridges the visitor's `BioContext` + `BioUserAccess` standing into the
 * community provider so the (bio-agnostic) join flow can gate on subscription
 * access without the community slice importing bio. Resolves the viewer's level
 * (synthesizing FOLLOWER from a plain follow) and, when a join needs a
 * subscription, opens the {@link ChannelSubscribeBlockerModal} — PopshopRN's
 * "Subscribe Now and Join" blocker.
 */
function VisitorCommunityProvider({
  bioContext,
  creatorName,
  creatorAvatar,
  creatorInfo,
  children,
}: {
  bioContext: BioContext;
  creatorName: string;
  creatorAvatar?: string;
  creatorInfo: CreatorInfo;
  children: ReactNode;
}) {
  const { extendedSubscriptionLevel, isFollowing } = useBioUserAccess();
  const [blockerChannel, setBlockerChannel] = useState<CommunityChannel | null>(
    null,
  );

  const viewerSubscriptionLevel =
    extendedSubscriptionLevel ??
    (isFollowing ? PopStoreExtendedSubscriptionLevel.FOLLOWER : undefined);

  const onRequireChannelSubscription = useCallback(
    (channel: CommunityChannel) => setBlockerChannel(channel),
    [],
  );

  return (
    <CommunityProvider
      creatorId={bioContext.creatorId}
      creatorName={creatorName}
      creatorInfo={creatorInfo}
      basePath={bioUrl(bioContext).communityChannels()}
      homeHref={bioUrl(bioContext).community()}
      bioContext={bioContext}
      viewerSubscriptionLevel={viewerSubscriptionLevel}
      onRequireChannelSubscription={onRequireChannelSubscription}
      // The visitor/member surface is view + participate only: an owner
      // previewing their own bio gets no management controls here (matching a
      // member's experience). Management routes to `/-/community` via the
      // "Manage" entry in the channel-list header.
      managementEnabled={false}
    >
      {children}
      <ChannelSubscribeBlockerModal
        channel={blockerChannel}
        creatorName={creatorName}
        creatorAvatar={creatorAvatar}
        bioContext={bioContext}
        onClose={() => setBlockerChannel(null)}
      />
    </CommunityProvider>
  );
}
